import { useState } from 'react';
import { X, Save, AlertCircle, Key, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface AccountMetaAdsModalProps {
  account: {
    id: string;
    platform: string;
    page_name: string;
    page_id: string;
    meta_app_id?: string | null;
    meta_app_secret?: string | null;
    meta_ad_account_id: string | null;
    meta_access_token: string | null;
    meta_token_expires_at?: string | null;
  };
  onClose: () => void;
  onSave: (accountId: string, adAccountId: string | null, accessToken: string | null) => void;
}

export default function AccountMetaAdsModal({ account, onClose, onSave }: AccountMetaAdsModalProps) {
  const [appId, setAppId] = useState(account.meta_app_id || '');
  const [appSecret, setAppSecret] = useState(account.meta_app_secret || '');
  const [adAccountId, setAdAccountId] = useState(account.meta_ad_account_id || '');
  const [accessToken, setAccessToken] = useState(account.meta_access_token || '');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);

  const testToken = async () => {
    try {
      setTesting(true);
      setTestResults(null);

      // Walidacja przed testem
      if (!adAccountId.trim() || !accessToken.trim()) {
        toast.error('Wypełnij oba pola przed testowaniem');
        setTesting(false);
        return;
      }

      let validatedId = adAccountId.trim();
      if (validatedId.includes('act=')) {
        validatedId = validatedId.replace('act=', 'act_');
        setAdAccountId(validatedId);
      }

      if (!validatedId.startsWith('act_')) {
        toast.error('ID konta musi zaczynać się od "act_"');
        setTesting(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        toast.error('Musisz być zalogowany');
        return;
      }

      toast.loading('Testowanie tokena...', { id: 'test' });

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/test-account-meta-ads-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            ad_account_id: validatedId,
            access_token: accessToken.trim(),
            app_id: appId.trim() || undefined,
            app_secret: appSecret.trim() || undefined,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to test token');
      }

      setTestResults(result);

      if (result.summary?.all_tests_passed) {
        toast.success('✅ Token jest poprawny!', { id: 'test' });
      } else {
        toast.error('❌ Token wymaga poprawy. Zobacz wyniki testów.', { id: 'test' });
      }
    } catch (error) {
      console.error('Error testing token:', error);
      toast.error(
        error instanceof Error ? error.message : 'Błąd podczas testowania tokena',
        { id: 'test' }
      );
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Walidacja - jeśli podano token, wymagane są również App credentials
      if (accessToken.trim() && (!appId.trim() || !appSecret.trim())) {
        toast.error('Jeśli podajesz token, musisz również podać Meta App ID i App Secret');
        setSaving(false);
        return;
      }

      // Walidacja formatu Ad Account ID
      if (adAccountId.trim()) {
        let validatedId = adAccountId.trim();

        // Zamień act= na act_
        if (validatedId.includes('act=')) {
          validatedId = validatedId.replace('act=', 'act_');
        }

        // Sprawdź format
        if (!validatedId.startsWith('act_')) {
          toast.error('ID konta reklamowego musi zaczynać się od "act_"');
          setSaving(false);
          return;
        }

        setAdAccountId(validatedId);
      }

      let finalAccessToken = accessToken.trim();
      let finalExpiresAt = account.meta_token_expires_at;

      // Jeśli użytkownik podał token i credentials, automatycznie wymień na long-lived
      if (accessToken.trim() && appId.trim() && appSecret.trim()) {
        toast.loading('Wymiana tokenu na długotrwały...', { id: 'exchange' });

        try {
          const { data: exchangeResult, error: exchangeError } = await supabase
            .rpc('exchange_meta_short_lived_token', {
              p_short_lived_token: accessToken.trim(),
              p_app_id: appId.trim(),
              p_app_secret: appSecret.trim(),
              p_user_id: null,
              p_account_id: account.id
            });

          if (exchangeError) throw exchangeError;

          toast.success('Token wymieniony na długotrwały (60 dni)!', { id: 'exchange' });
          finalAccessToken = exchangeResult.access_token;
          finalExpiresAt = exchangeResult.expires_at;
        } catch (exchangeError) {
          console.error('Token exchange error:', exchangeError);
          toast.error(
            `Błąd wymiany tokenu: ${exchangeError instanceof Error ? exchangeError.message : 'Nieznany błąd'}`,
            { id: 'exchange' }
          );
          // Kontynuuj zapisywanie nawet jeśli wymiana się nie powiodła
        }
      }

      // Zapisz do bazy
      const { error } = await supabase
        .from('social_accounts')
        .update({
          meta_app_id: appId.trim() || null,
          meta_app_secret: appSecret.trim() || null,
          meta_ad_account_id: adAccountId.trim() || null,
          meta_access_token: finalAccessToken || null,
          meta_token_expires_at: finalExpiresAt,
        })
        .eq('id', account.id);

      if (error) throw error;

      toast.success('Ustawienia Meta Ads zapisane dla tego konta');
      setTestResults(null);
      onSave(account.id, adAccountId.trim() || null, finalAccessToken || null);
      onClose();
    } catch (error) {
      console.error('Error saving account Meta Ads settings:', error);
      toast.error('Błąd podczas zapisywania ustawień');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    try {
      setSaving(true);

      const { error } = await supabase
        .from('social_accounts')
        .update({
          meta_app_id: null,
          meta_app_secret: null,
          meta_ad_account_id: null,
          meta_access_token: null,
          meta_token_expires_at: null,
        })
        .eq('id', account.id);

      if (error) throw error;

      toast.success('Ustawienia zostały wyczyszczone. Będą używane globalne ustawienia.');
      onSave(account.id, null, null);
      onClose();
    } catch (error) {
      console.error('Error clearing account Meta Ads settings:', error);
      toast.error('Błąd podczas czyszczenia ustawień');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl border border-slate-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div>
            <h2 className="text-xl font-bold text-white">Ustawienia Meta Ads</h2>
            <p className="text-sm text-slate-400 mt-1">{account.page_name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Info Box */}
          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-300 space-y-1">
                <p className="font-medium">Nadpisanie globalnych ustawień</p>
                <p>
                  Jeśli to konto jest w innym Business Manager niż pozostałe, możesz tu podać dedykowane
                  credentials Meta App, ID konta reklamowego i token dostępu.
                </p>
                <p className="text-blue-400">
                  Jeśli zostawisz puste, system użyje globalnych ustawień z sekcji "Integracja Meta Ads API".
                </p>
              </div>
            </div>
          </div>

          {/* Meta App Credentials (Optional) */}
          <div className="border-t border-slate-700 pt-4">
            <p className="text-sm font-medium text-slate-300 mb-3">Meta App Credentials (opcjonalne)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Meta App ID
                </label>
                <input
                  type="text"
                  placeholder="1234567890123456 (lub puste dla globalnego)"
                  value={appId}
                  onChange={(e) => setAppId(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Meta App Secret
                </label>
                <input
                  type="password"
                  placeholder="abc123... (lub puste dla globalnego)"
                  value={appSecret}
                  onChange={(e) => setAppSecret(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  autoComplete="new-password"
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                />
              </div>
            </div>
          </div>

          {/* Account Data */}
          <div className="border-t border-slate-700 pt-4">
            <p className="text-sm font-medium text-slate-300 mb-3">Dane konta reklamowego</p>
          </div>

          {/* Ad Account ID */}
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              ID Konta Reklamowego
            </label>
            <input
              type="text"
              placeholder="act_1234567890 (lub zostaw puste dla globalnych)"
              value={adAccountId}
              onChange={(e) => setAdAccountId(e.target.value)}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
            />
            <p className="text-xs text-slate-400 mt-1">
              Format: <code className="bg-slate-700 px-1 rounded">act_XXXXXXXXXX</code>
            </p>
          </div>

          {/* Access Token */}
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Token Dostępu Meta Ads
            </label>
            <textarea
              placeholder="EAABsb... (lub zostaw puste dla globalnego)"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs resize-none"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
            />
            <p className="text-xs text-slate-400 mt-1">
              Token musi mieć uprawnienia: ads_management, pages_read_engagement
            </p>
          </div>

          {/* Test Token Button */}
          <div>
            <button
              onClick={testToken}
              disabled={testing || !adAccountId.trim() || !accessToken.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 text-white rounded-lg transition-colors"
            >
              <CheckCircle className="w-4 h-4" />
              {testing ? 'Testowanie...' : 'Przetestuj Token i Uprawnienia'}
            </button>
            <p className="text-xs text-slate-400 mt-1">
              Sprawdzi czy token ma dostęp do tego konta reklamowego
            </p>
          </div>

          {/* Test Results */}
          {testResults && (
            <div className="space-y-2">
              <div className={`p-4 rounded-lg border ${
                testResults.summary?.all_tests_passed
                  ? 'bg-green-500/10 border-green-500/30'
                  : 'bg-red-500/10 border-red-500/30'
              }`}>
                <p className={`font-medium ${
                  testResults.summary?.all_tests_passed ? 'text-green-300' : 'text-red-300'
                }`}>
                  {testResults.summary?.recommendation}
                </p>
              </div>

              {/* Token Info */}
              {testResults.token_info && (
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <p className="text-sm font-medium text-slate-200 mb-2">Informacje o tokenie:</p>
                  <ul className="text-xs text-slate-300 space-y-1">
                    <li>✓ App ID: {testResults.token_info.app_id}</li>
                    <li>✓ Ważny: {testResults.token_info.is_valid ? 'Tak' : 'Nie'}</li>
                    {testResults.token_info.scopes && (
                      <li>✓ Uprawnienia: {testResults.token_info.scopes.join(', ')}</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Ad Account Info */}
              {testResults.ad_account_name && (
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <p className="text-sm font-medium text-slate-200 mb-2">Konto reklamowe:</p>
                  <ul className="text-xs text-slate-300 space-y-1">
                    <li>✓ Nazwa: {testResults.ad_account_name}</li>
                    <li>✓ Status: {testResults.ad_account_status === 1 ? 'Aktywne' : 'Nieaktywne'}</li>
                  </ul>
                </div>
              )}

              {/* Errors */}
              {!testResults.summary?.all_tests_passed && (
                <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                  <p className="text-sm font-medium text-red-300 mb-2">Problemy:</p>
                  <ul className="text-xs text-red-200 space-y-1">
                    {Object.entries(testResults.tests).map(([key, test]: [string, any]) => {
                      if (!test.success && test.data?.error) {
                        return (
                          <li key={key}>
                            • {key}: {test.data.error.message || JSON.stringify(test.data.error)}
                          </li>
                        );
                      }
                      return null;
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Status */}
          {(account.meta_ad_account_id || account.meta_access_token) && !testResults && (
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-sm text-green-300">
                <Key className="w-4 h-4 inline mr-2" />
                To konto ma własne ustawienia Meta Ads (nadpisują globalne)
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-slate-700 bg-slate-900/50">
          <button
            onClick={handleClear}
            disabled={saving || (!account.meta_ad_account_id && !account.meta_access_token)}
            className="px-4 py-2 text-sm text-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Wyczyść (użyj globalnych)
          </button>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
            >
              Anuluj
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 text-white rounded-lg transition-colors"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
